import { useState } from 'react';
import axios from 'axios';
import ButtonSymphony from 'elements/ButtonSymphony';
import InputSymphony from 'elements/InputSymphony';
import ModalSymphony from 'elements/ModalSymphony';

interface ContentAnalyzerModalProps {
    /** Whether the modal is currently open */
    isOpen: boolean;
    /** Function to close the modal */
    onClose: () => void;
}

const ContentAnalyzerModal = ({
    isOpen,
    onClose
}: ContentAnalyzerModalProps) => {
    /** Instagram handle input state */
    const [instagramHandle, setInstagramHandle] = useState<string>('');
    /** Loading state for the analysis process */
    const [isLoading, setIsLoading] = useState<boolean>(false);
    /** Analysis result data */
    const [analysisResult, setAnalysisResult] = useState<any>(null);
    /** Error message if analysis fails */
    const [error, setError] = useState<string | null>(null);

    /** Handles the Instagram analysis submission */
    const handleAnalyzeInstagram = async () => {
        if (!instagramHandle.trim()) {
            setError('Please enter an Instagram handle');
            return;
        }

        setIsLoading(true);
        setError(null);

        try {
            const response = await axios.post(`${process.env.REACT_APP_API_URL}/agents/analyze-instagram-videos`, {
                instagramHandle: instagramHandle.trim()
            });

            setAnalysisResult(response.data);
        } catch (err) {
            setError('Failed to analyze Instagram videos. Please try again.');
            console.error('Error analyzing Instagram:', err);
        } finally {
            setIsLoading(false);
        }
    };

    /** Renders the content of the modal */
    const renderContent = () => {
        return (
            <div className="contentAnalyzerModalContent">
                {!analysisResult ? (
                    <div className="inputSection">
                        <InputSymphony
                            className="contentAnalyzerModalInput"
                            label="Enter your Instagram"
                            value={instagramHandle}
                            onChange={(value) => setInstagramHandle(value)}
                            placeholder="@username"
                        />
                        <ButtonSymphony
                            className="contentAnalyzerModalButton"
                            onClick={handleAnalyzeInstagram}
                            isLoading={isLoading}
                            disabled={isLoading}
                        >
                            Enter
                        </ButtonSymphony>
                    </div>
                ) : (
                    <div className="resultsSection">
                        <h3>Analysis Results</h3>
                        <pre>{JSON.stringify(analysisResult, null, 2)}</pre>
                        <ButtonSymphony
                            className="contentAnalyzerModalButton"
                            onClick={() => setAnalysisResult(null)}
                        >
                            Analyze Another Account
                        </ButtonSymphony>
                    </div>
                )}
            </div>
        );
    };

    return (
        <ModalSymphony
            className="contentAnalyzerModal"
            isOpen={isOpen}
            onClose={onClose}
            title="Instagram Content Analyzer"
            size="medium"
        >
            {renderContent()}
        </ModalSymphony>
    );
};

export default ContentAnalyzerModal;

/**
 * Example usage of the ContentAnalyzerModal component:
 * 
 * // In your component:
 * const { ContentAnalyzerModal, showContentAnalyzerModal } = useContentAnalyzerModal();
 * 
 * // In your JSX:
 * return (
 *   <>
 *     <ButtonSymphony onClick={showContentAnalyzerModal}>
 *       Analyze Instagram Content
 *     </ButtonSymphony>
 *     {ContentAnalyzerModal}
 *   </>
 * );
 */